import React from "react";
import { AlertTriangle, X } from "lucide-react";
import { useUiLang } from "../../../context/UiLangContext";

interface StopConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function StopConfirmModal({ isOpen, onClose, onConfirm }: StopConfirmModalProps) {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  const [isPending, setIsPending] = React.useState(false);

  // Reset pending state when modal is opened/closed
  React.useEffect(() => {
    if (isOpen) {
      setIsPending(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPending) return;
    
    setIsPending(true);
    onConfirm();
    
    // Delay closing to prevent touch/click events from bleeding through to underlying elements
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPending) return;
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className={`bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-sm w-full shadow-2xl overflow-hidden animate-[modal-appear_0.18s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)] transition-opacity duration-200 ${
          isPending ? "pointer-events-none opacity-80" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--surface)] border-b border-[var(--border)]">
          <div className="flex items-center gap-2 text-[var(--danger)]">
            <AlertTriangle size={18} />
            <h2 className="text-xs font-bold uppercase tracking-wider font-mono">
              {isEn ? "End Session?" : "¿Finalizar Grabación?"}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="w-6 h-6 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all disabled:opacity-50"
            aria-label={isEn ? "Close" : "Cerrar"}
          >
            <X size={14} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col gap-4">
          <p className="text-xs text-[var(--text-muted)] leading-relaxed">
            {isEn
              ? "Are you sure you want to end this recording? Will be stopped and saved. You won't be able to resume this same recording later."
              : "¿Estás seguro de que querés finalizar la grabación? Se detendrá y se guardará. No podrás reanudar esta misma grabación más tarde."}
          </p>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-[var(--border)]">
            <button
              onClick={handleClose}
              disabled={isPending}
              className="px-4 py-2 text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text)] transition-all bg-transparent hover:bg-[var(--surface)] rounded-lg cursor-pointer disabled:opacity-50"
            >
              {isEn ? "Cancel" : "Cancelar"}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isPending}
              className="px-4 py-2 text-xs font-semibold text-white bg-[var(--danger)] hover:bg-[#e03b30] active:scale-95 rounded-lg shadow-lg shadow-red-500/10 transition-all cursor-pointer disabled:opacity-50"
            >
              {isEn ? "End Recording" : "Sí, finalizar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
